import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Users, Crown, CreditCard, BarChart3, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const AdminTest = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [adminToken, setAdminToken] = useState('');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const loginAsAdmin = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/admin/test-login');
      const { access_token, user } = response.data;
      
      setAdminToken(access_token);
      setIsLoggedIn(true);
      
      // Set authorization header for future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success(`Admin olarak giriş yapıldı: ${user.name}`);
    } catch (error) {
      console.error('Admin login error:', error);
      toast.error('Admin girişi başarısız');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const [statsResponse, usersResponse] = await Promise.all([
        axios.get('/api/admin/stats'),
        axios.get('/api/admin/users')
      ]);
      
      setStats(statsResponse.data);
      setUsers(usersResponse.data);
      toast.success('Admin verileri yüklendi');
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center space-x-3 mb-4"
          >
            <Shield className="w-12 h-12 text-red-400" />
            <h1 className="text-4xl font-bold text-white">Admin Panel Test</h1>
          </motion.div>
          <p className="text-gray-400 text-lg">
            Premium yönetimi ve admin işlevlerini test edin
          </p>
        </div>

        {/* Login Section */}
        {!isLoggedIn ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-800 rounded-2xl p-8 text-center"
          >
            <h2 className="text-2xl font-bold text-white mb-4">Admin Girişi</h2>
            <p className="text-gray-400 mb-6">
              Admin panelini test etmek için admin olarak giriş yapın
            </p>
            <button
              onClick={loginAsAdmin}
              disabled={loading}
              className="bg-red-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-600 disabled:opacity-50 transition-all duration-200 flex items-center space-x-2 mx-auto"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <Shield className="w-5 h-5" />
              )}
              <span>Admin Olarak Giriş Yap</span>
            </button>
          </motion.div>
        ) : (
          <div className="space-y-8">
            {/* Success Message */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 flex items-center space-x-3"
            >
              <CheckCircle className="w-6 h-6 text-green-400" />
              <div>
                <h3 className="text-green-400 font-semibold">Admin Girişi Başarılı</h3>
                <p className="text-gray-300 text-sm">Admin paneli özelliklerine erişebilirsiniz</p>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <button
                onClick={fetchAdminData}
                disabled={loading}
                className="bg-blue-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-600 disabled:opacity-50 transition-all duration-200 flex items-center space-x-2"
              >
                <BarChart3 className="w-5 h-5" />
                <span>Verileri Yükle</span>
              </button>
              <button
                onClick={() => {
                  setIsLoggedIn(false);
                  setAdminToken('');
                  setStats(null);
                  setUsers([]);
                  delete axios.defaults.headers.common['Authorization'];
                  toast.success('Admin çıkışı yapıldı');
                }}
                className="bg-gray-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-600 transition-all duration-200"
              >
                Çıkış Yap
              </button>
            </div>

            {/* Stats Section */}
            {stats && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
              >
                <div className="bg-gray-800 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">Toplam Kullanıcı</h3>
                    <Users className="w-8 h-8 text-blue-400" />
                  </div>
                  <p className="text-3xl font-bold text-white">{stats.users.total}</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {stats.users.premium} premium, {stats.users.free} ücretsiz
                  </p>
                </div>

                <div className="bg-gray-800 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">Premium Oranı</h3>
                    <Crown className="w-8 h-8 text-yellow-400" />
                  </div>
                  <p className="text-3xl font-bold text-white">%{stats.users.premium_percentage}</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {stats.users.premium} / {stats.users.total} kullanıcı
                  </p>
                </div>

                <div className="bg-gray-800 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">Toplam Gelir</h3>
                    <CreditCard className="w-8 h-8 text-green-400" />
                  </div>
                  <p className="text-3xl font-bold text-white">{formatPrice(stats.revenue.total)}</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Aylık: {formatPrice(stats.revenue.monthly)}
                  </p>
                </div>

                <div className="bg-gray-800 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">Aktif Abonelik</h3>
                    <BarChart3 className="w-8 h-8 text-purple-400" />
                  </div>
                  <p className="text-3xl font-bold text-white">{stats.subscriptions.active}</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {stats.subscriptions.total} toplam abonelik
                  </p>
                </div>
              </motion.div>
            )}

            {/* Users Table */}
            {users.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-800 rounded-xl overflow-hidden"
              >
                <div className="p-6 border-b border-gray-700">
                  <h3 className="text-xl font-semibold text-white">Kullanıcılar</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Kullanıcı</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Durum</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Abonelik</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Toplam Ödeme</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Kayıt Tarihi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {users.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-700/50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                                <span className="text-white font-semibold">
                                  {user.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-white">{user.name}</div>
                                <div className="text-sm text-gray-400">{user.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {user.is_premium ? (
                                <Crown className="w-4 h-4 text-yellow-400" />
                              ) : (
                                <Users className="w-4 h-4 text-gray-400" />
                              )}
                              <span className={`text-sm ${user.is_premium ? 'text-yellow-400' : 'text-gray-400'}`}>
                                {user.is_premium ? 'Premium' : 'Ücretsiz'}
                              </span>
                              {user.is_admin && (
                                <span className="bg-red-500 text-white text-xs px-2 py-1 rounded">Admin</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {user.active_subscription ? (
                              <div>
                                <div className="text-sm text-white">{user.active_subscription.plan_name}</div>
                                <div className="text-xs text-gray-400">
                                  {user.active_subscription.billing_cycle === 'yearly' ? 'Yıllık' : 'Aylık'}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                            {formatPrice(user.total_payments)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                            {formatDate(user.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTest;
